import { z } from 'zod';

/**
 * Kanał powiadomień (docs/16-powiadomienia.md). Każde powiadomienie należy do grupy; pracownik wybiera dla każdej grupy,
 * gdzie je dostaje: dzwonek w panelu, aplikacja mobilna, e-mail z podsumowaniem dnia. Powiadomienia są wyliczane ze stanu
 * (bez kolejki) — ten sam identyfikator w panelu i na telefonie, więc przeczytanie w jednym miejscu działa wszędzie.
 *
 * Telefon: dziś dzwonek w aplikacji, odświeżany przy synchronizacji. Później dojdzie push (FCM) jako kolejny sposób
 * dostarczenia na kanale „telefon” — bez zmiany grup, ustawień ani identyfikatorów.
 */
export const NOTIFICATION_GROUPS = {
  sprawy: { label: 'Sprawy i klienci', hint: 'oferty, decyzje klientów, podpisy, zamknięcie spraw' },
  zadania: { label: 'Zadania w sprawach', hint: 'zadania dla Ciebie, kolejka do przejęcia, zastępstwa, po terminie' },
  teren: { label: 'Prace w terenie', hint: 'nowe prace i zmiany terminów, przypomnienia, zakończenia, blokady, protokoły' },
  kadry: { label: 'Kadry', hint: 'wnioski o nieobecność i decyzje, wydarzenia firmowe' },
  finanse: { label: 'Finanse', hint: 'wpłaty, płatności po terminie, faktury zakupu z KSeF' },
  nadzor: { label: 'Nadzór', hint: 'sprawy, które utknęły, zamknięcia wyjątkowe, oferty poniżej progu' },
  serwis: { label: 'Serwis', hint: 'nowe zgłoszenia do umówienia (pilne także na telefon), zaległe przeglądy raz dziennie' },
  flota: { label: 'Flota', hint: 'terminy i limity przebiegu pojazdów, zgłoszenia usterek, konflikty niedostępności, import paliwa' },
  magazyn: { label: 'Magazyn', hint: 'zgłoszenia materiałów od ekip, braki i ryzyko terminów, pakiety, dostawy po terminie' },
} as const;
export type NotificationGroup = keyof typeof NOTIFICATION_GROUPS;
export const NOTIFICATION_GROUP_KEYS = Object.keys(NOTIFICATION_GROUPS) as NotificationGroup[];

export const NOTIFICATION_CHANNELS = {
  panel: 'Dzwonek w panelu',
  telefon: 'Aplikacja mobilna',
  email: 'E-mail — podsumowanie dnia',
} as const;
export type NotificationChannel = keyof typeof NOTIFICATION_CHANNELS;
export const NOTIFICATION_CHANNEL_KEYS = Object.keys(NOTIFICATION_CHANNELS) as NotificationChannel[];

export type NotificationSettings = Record<NotificationGroup, Record<NotificationChannel, boolean>>;

/** Domyślnie: wszystko w panelu; na telefonie — praca w terenie i kadry; bez e-maili. */
export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  sprawy: { panel: true, telefon: false, email: false },
  zadania: { panel: true, telefon: false, email: false },
  teren: { panel: true, telefon: true, email: false },
  kadry: { panel: true, telefon: true, email: false },
  finanse: { panel: true, telefon: false, email: false },
  nadzor: { panel: true, telefon: false, email: false },
  // pilne zgłoszenia (priorytet wysoki / pilny) trafiają na telefon także bez zaznaczenia — `NotificationDto.urgent`
  serwis: { panel: true, telefon: false, email: false },
  flota: { panel: true, telefon: false, email: false },
  magazyn: { panel: true, telefon: false, email: false },
};

/** Grupy modułów opcjonalnych — w ustawieniach widoczne tylko przy włączonym module. */
export const NOTIFICATION_GROUP_MODULE: Partial<Record<NotificationGroup, 'flota' | 'magazyn'>> = { flota: 'flota', magazyn: 'magazyn' };

/** Zapisane ustawienia uzupełnione domyślnymi (nowe grupy i kanały działają bez migracji danych). */
export function notificationSettings(stored: unknown): NotificationSettings {
  const s = (stored && typeof stored === 'object' ? stored : {}) as Partial<Record<string, Partial<Record<string, unknown>>>>;
  const out = {} as NotificationSettings;
  for (const g of NOTIFICATION_GROUP_KEYS) {
    out[g] = { ...DEFAULT_NOTIFICATION_SETTINGS[g] };
    for (const c of NOTIFICATION_CHANNEL_KEYS) if (typeof s[g]?.[c] === 'boolean') out[g][c] = s[g]![c] as boolean;
  }
  return out;
}

export const notificationSettingsSchema = z.object(
  Object.fromEntries(NOTIFICATION_GROUP_KEYS.map((g) => [g, z.object({ panel: z.boolean(), telefon: z.boolean(), email: z.boolean() })])) as Record<
    NotificationGroup,
    z.ZodObject<{ panel: z.ZodBoolean; telefon: z.ZodBoolean; email: z.ZodBoolean }>
  >,
);

export interface NotificationSettingsDto {
  settings: NotificationSettings;
  /** adres podsumowania e-mail (konto pracownika); null — e-mail niedostępny */
  email: string | null;
  /** push na telefon jeszcze niedostępny — powiadomienia „telefon” widać w aplikacji po synchronizacji */
  pushAvailable: boolean;
}

export const notificationReadSchema = z.object({ ids: z.array(z.string().regex(/^[a-z][\w-]{1,58}$/)).min(1).max(200) });

/** Odmiana: 1 zadanie, 2 zadania, 5 zadań. */
export function plPlural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  const d = n % 10;
  const t = n % 100;
  return d >= 2 && d <= 4 && (t < 12 || t > 14) ? few : many;
}
